import { useEffect } from "react";
import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading2,
  Link as LinkIcon,
  Quote,
} from "lucide-react";

interface Props {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

export default function RichTextEditor({
  value,
  onChange,
  placeholder,
  disabled,
}: Props) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          rel: "noopener noreferrer nofollow",
          target: "_blank",
        },
      }),
    ],
    content: value || "",
    editable: !disabled,
    onUpdate({ editor }) {
      const html = editor.getHTML();
      // Tiptap returns "<p></p>" for an empty doc — normalise to "" so the
      // dirty check and save-as-null path work identically to a textarea.
      onChange(html === "<p></p>" ? "" : html);
    },
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none min-h-[200px] px-3 py-2 focus:outline-none text-neutral-800",
        "data-placeholder": placeholder ?? "",
      },
    },
  });

  // Sync external value resets (e.g. when the form swaps which club is loaded).
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value || "";
    if (current === next) return;
    if (current === "<p></p>" && next === "") return;
    editor.commands.setContent(next, { emitUpdate: false });
  }, [value, editor]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  return (
    <div className="rounded-md border border-neutral-200 bg-white overflow-hidden">
      <Toolbar editor={editor} disabled={disabled} />
      <EditorContent editor={editor} />
    </div>
  );
}

function Toolbar({
  editor,
  disabled,
}: {
  editor: Editor | null;
  disabled?: boolean;
}) {
  if (!editor) return null;
  const btn = (active: boolean) =>
    `inline-flex items-center justify-center w-8 h-8 rounded text-neutral-600 hover:bg-neutral-100 disabled:opacity-40 disabled:cursor-not-allowed ${
      active ? "bg-neutral-100 text-neutral-900" : ""
    }`;
  return (
    <div className="flex items-center gap-1 border-b border-neutral-200 bg-neutral-50/50 px-2 py-1">
      <button
        type="button"
        className={btn(editor.isActive("bold"))}
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={disabled}
        aria-label="Bold"
      >
        <Bold className="w-4 h-4" />
      </button>
      <button
        type="button"
        className={btn(editor.isActive("italic"))}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={disabled}
        aria-label="Italic"
      >
        <Italic className="w-4 h-4" />
      </button>
      <button
        type="button"
        className={btn(editor.isActive("heading", { level: 2 }))}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        disabled={disabled}
        aria-label="Heading"
      >
        <Heading2 className="w-4 h-4" />
      </button>
      <span className="w-px h-5 bg-neutral-200 mx-1" />
      <button
        type="button"
        className={btn(editor.isActive("bulletList"))}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        disabled={disabled}
        aria-label="Bullet list"
      >
        <List className="w-4 h-4" />
      </button>
      <button
        type="button"
        className={btn(editor.isActive("orderedList"))}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        disabled={disabled}
        aria-label="Numbered list"
      >
        <ListOrdered className="w-4 h-4" />
      </button>
      <button
        type="button"
        className={btn(editor.isActive("blockquote"))}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        disabled={disabled}
        aria-label="Quote"
      >
        <Quote className="w-4 h-4" />
      </button>
      <span className="w-px h-5 bg-neutral-200 mx-1" />
      <button
        type="button"
        className={btn(editor.isActive("link"))}
        onClick={() => promptLink(editor)}
        disabled={disabled}
        aria-label="Link"
      >
        <LinkIcon className="w-4 h-4" />
      </button>
    </div>
  );
}

function promptLink(editor: Editor) {
  const prev = editor.getAttributes("link").href ?? "";
  // eslint-disable-next-line no-alert
  const url = window.prompt("URL", prev);
  if (url === null) return;
  if (url === "") {
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    return;
  }
  editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
}
